import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import { Badge, Button, Icon, IconButton, NumberInput, ScopeSelect, Select } from "../../components/ui";
import { currencyOptions } from "../../core/currency";
import { duplicateYears, toEditableBands, toPayload } from "./buyTargetBands.js";

/**
 * The buy-target editor for ONE Core List reference.
 *
 * Alex's mental model is bands — "2021 & 2022 = max 210,000 HKD, 2024 & 2025 =
 * max 230,000" — so that is what this edits. Storage is one row per year (see
 * the core_list_price_targets migration), and the two shapes are converted at
 * this boundary: bands are expanded on save, and regrouped on load by
 * (max_price, currency). A band is therefore a pure UI grouping and nothing
 * downstream has to parse a range.
 *
 * Props:
 *   - itemId        core_list_item_id
 *   - targets       the reference's saved targets (year-ascending, from the API)
 *   - onSaved()     parent refetch trigger
 */

// USDT is deliberately absent: the backend validates against
// Currencies::baseChoices(), which excludes it, so offering it here would
// produce a 422 the user cannot act on. Everything else currencyOptions()
// serves is base-eligible and has a symbol the Money primitive can render.
const targetCurrencyOptions = () =>
  currencyOptions().filter((option) => option.id !== "usdt");

const YEAR_SPAN = 45;

const yearOptions = () => {
  const latest = new Date().getFullYear() + 1;
  return Array.from({ length: YEAR_SPAN }, (_, index) => {
    const year = latest - index;
    return { key: String(year), label: String(year) };
  });
};

const emptyBand = (currency) => ({ years: [], maxPrice: "", currency });

/**
 * @param {node} beforeActions  Rendered between the price bands and the Save
 *   row. Core List puts "Where to look" here so the sources and filters sit
 *   ABOVE the button that saves them (Alex, 2026-10-12) — one decision, read
 *   top to bottom, ending in the single click that persists all of it.
 */
const BuyTargetEditor = ({ itemId, targets, onSaved, alsoSave, beforeActions = null }) => {
  const currencies = useMemo(targetCurrencyOptions, []);
  const defaultCurrency = currencies[0]?.id || "gbp";
  const years = useMemo(yearOptions, []);

  const [bands, setBands] = useState(() => {
    const existing = toEditableBands(targets);
    return existing.length ? existing : [emptyBand(defaultCurrency)];
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  // A useState initialiser runs ONCE. The panel can open before
  // /api/core-list/targets has resolved, in which case the editor seeded itself
  // from an empty `targets` and then never picked up the saved bands — the
  // reference showed its target in the row while the editor claimed there was
  // none. Re-seed whenever the saved set actually changes identity, but only
  // while the user has nothing in flight, so a refetch can never overwrite
  // something half-typed.
  const savedSignature = useMemo(
    () =>
      (targets || [])
        .map((t) => `${t.year}:${t.max_price}:${t.currency}`)
        .join("|"),
    [targets]
  );
  const appliedSignature = useRef(savedSignature);

  useEffect(() => {
    if (appliedSignature.current === savedSignature || saving) return;
    appliedSignature.current = savedSignature;
    const existing = toEditableBands(targets);
    setBands(existing.length ? existing : [emptyBand(defaultCurrency)]);
  }, [savedSignature, targets, saving, defaultCurrency]);

  // Each editing action retracts the confirmation — it should say "Saved"
  // about what is on screen, not about something already changed since. This
  // is deliberately hung off the USER's actions rather than off `bands`: a
  // successful save refetches and re-seeds the bands, so watching that value
  // would clear the message in the same tick it appeared.
  const patchBand = useCallback((index, patch) => {
    setSaved(false);
    setBands((current) =>
      current.map((band, i) => (i === index ? { ...band, ...patch } : band))
    );
  }, []);

  const addBand = useCallback(() => {
    setSaved(false);
    setBands((current) => [...current, emptyBand(defaultCurrency)]);
  }, [defaultCurrency]);

  const removeBand = useCallback((index) => {
    setSaved(false);
    setBands((current) => current.filter((_, i) => i !== index));
  }, []);

  const duplicates = useMemo(() => duplicateYears(bands), [bands]);

  /**
   * Is this band exactly what the server has?
   *
   * Compared by VALUE against the saved set rather than tracked as state: a
   * band edited back to its saved figures is saved again, and a refetch cannot
   * leave a stale flag behind. The years are sorted before comparing, because
   * picking 2024 then 2023 is the same band as picking them the other way
   * round and must not read as unsaved.
   */
  const savedKeys = useMemo(() => {
    const key = (b) =>
      `${[...(b.years || [])].map(Number).sort((x, y) => x - y).join(",")}|` +
      `${Number(b.maxPrice) || 0}|${String(b.currency || "").toLowerCase()}`;

    return new Set(toEditableBands(targets).map(key));
  }, [targets]);

  const isSaved = (band) => {
    if (!band.years?.length || !band.maxPrice) return false;

    const key =
      `${[...band.years].map(Number).sort((x, y) => x - y).join(",")}|` +
      `${Number(band.maxPrice) || 0}|${String(band.currency || "").toLowerCase()}`;

    return savedKeys.has(key);
  };

  // A band with years but no price, or a price but no years, is half-entered —
  // saving it would silently drop the half the user did fill in.
  const incomplete = bands.some(
    (band) =>
      (band.years?.length > 0) !== (band.maxPrice !== "" && Number(band.maxPrice) > 0)
  );

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await axios.put(`/api/core-list/${itemId}/targets`, {
        // Bands the user emptied are dropped rather than sent as zero-year
        // entries; sending the complete remaining set is what deletes them
        // server-side.
        targets: toPayload(bands.filter((band) => band.years?.length && band.maxPrice)),
      });

      // Where to look is saved in the SAME click, after the targets land
      // (Alex, 2026-10-09: "the sources and filters have to be saved when the
      // target is set not after"). After, not before, because the marketplace
      // searches are built FROM the bands — saving sources against targets
      // that then failed to save would ask the scraper for a ceiling that
      // does not exist.
      if (alsoSave?.current) {
        await alsoSave.current();
      }

      setSaved(true);
      onSaved && onSaved();
    } catch (e) {
      setError(
        e?.response?.data?.message || "Couldn't save the buy targets. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  // The confirmation clears itself after a few seconds. Kept in an effect
  // rather than a setTimeout inside onSave so that unmounting the panel —
  // closing it right after saving — cancels the timer instead of setting state
  // on a component that is gone.
  useEffect(() => {
    if (!saved) return undefined;
    const timer = setTimeout(() => setSaved(false), 2500);
    return () => clearTimeout(timer);
  }, [saved]);


  return (
    <div className="buy-target-editor">
      {/* Each control carries its own visible label. They were three bare
          boxes in a row with the word "Max" floating between two of them, so
          nothing said which was the year picker and which the currency until
          you read their values — and an empty band said nothing at all
          (Alex, 2026-09-25). The labels are `aria-hidden`: every control
          already has its own accessible name, and a visible label that is not
          a <label> would otherwise be read out twice. */}
      {bands.map((band, index) => (
        <div
          className={`buy-target-editor__band${
            isSaved(band) ? " is-saved" : " is-draft"
          }`}
          key={index}
        >
          {/* Saved or not, said on the row itself. Every band rendered
              identically, so a target you saved last week and a line you are
              part-way through typing were indistinguishable — Alex could not
              tell which was which (2026-10-09). The state is DERIVED by
              comparing against what the server sent, not tracked separately,
              so it cannot drift out of step with a save or a refetch. */}
          <Badge
            className="buy-target-editor__state"
            tone={isSaved(band) ? "success" : "accent"}
            size="sm"
            title={
              isSaved(band)
                ? "Saved — this band is live and being matched"
                : "Not saved yet"
            }
          >
            {isSaved(band) ? "Saved" : "Draft"}
          </Badge>

          <div className="buy-target-editor__field buy-target-editor__field--years">
            <span className="buy-target-editor__field-label" aria-hidden="true">Years</span>
            <ScopeSelect
              label="Years"
              size="md"
              placeholder="Select years"
              searchable
              options={years}
              value={band.years}
              onChange={(next) => patchBand(index, { years: next || [] })}
              summary={(picked) =>
                picked.length > 3
                  ? `${picked.length} years`
                  : picked.map((option) => option.label).join(" & ")
              }
            />
          </div>

          <div className="buy-target-editor__field buy-target-editor__field--price">
            <span className="buy-target-editor__field-label" aria-hidden="true">Max price</span>
            {/* `money` + `currency` render the band's own symbol as the prefix,
                so the field reads "HK$ 210,000" while it is being typed — the
                figure the merchant is actually thinking in. The placeholder is
                grouped for the same reason: it is a worked example, and an
                ungrouped 210000 reads as a different number at a glance. */}
            <NumberInput
              value={band.maxPrice === "" ? null : Number(band.maxPrice)}
              onChange={(value) => patchBand(index, { maxPrice: value ?? "" })}
              money
              currency={band.currency}
              min={1}
              placeholder="210,000"
              aria-label="Maximum buy price"
            />
          </div>

          <div className="buy-target-editor__field buy-target-editor__field--currency">
            <span className="buy-target-editor__field-label" aria-hidden="true">Currency</span>
            <Select
              label="Target currency"
              options={currencies.map((option) => ({
                value: option.id,
                label: option.name,
              }))}
              value={band.currency}
              onChange={(value) => patchBand(index, { currency: value })}
            />
          </div>

          {/* Always, including on the last band. Hiding it below two bands
              meant a saved target could never be REMOVED — only edited into
              something else — so a reference you stopped wanting kept
              notifying you forever (Alex, 2026-10-09). Removing the last band
              and saving clears the target and its matches with it. */}
          <IconButton
            className="buy-target-editor__remove"
            size="md"
            variant="ghost"
            onClick={() => removeBand(index)}
            label={
              isSaved(band)
                ? `Remove the saved ${band.years?.join(", ")} target`
                : "Remove this price band"
            }
          >
            <Icon name="Trash2" size="sm" />
          </IconButton>
        </div>
      ))}

      {/* Every band removed. Said plainly, because an empty editor otherwise
          looks like it failed to load — and because saving from here is a
          DELETE: it clears the target and everything matched against it. */}
      {bands.length === 0 && (
        <p className="buy-target-editor__cleared">
          {savedKeys.size > 0
            ? "Saving now removes this reference's buy target and the matches under it."
            : "No price bands. Add one to start watching this reference."}
        </p>
      )}

      {/* Directly under the bands it adds to (Guillaume, 2026-10-12: it sat
          beside Save, below "Where to look", which read as adding a source). */}
      <Button
        className="buy-target-editor__add"
        size="sm"
        variant="ghost"
        onClick={addBand}
      >
        <Icon name="Plus" size="sm" /> Add price band
      </Button>

      {beforeActions}

      <div className="buy-target-editor__footer">
        <Button
          size="md"
          variant="primary"
          onClick={onSave}
          disabled={saving || duplicates.length > 0 || incomplete}
        >
          {/* The button says what the click will DO. Saving an empty editor
              deletes the target, and "Save targets" over a cleared form reads
              as a no-op rather than as a removal. */}
          {saving
            ? "Saving…"
            : bands.length === 0 && savedKeys.size > 0
              ? "Remove target"
              : "Save targets"}
        </Button>
      </div>

      {/* Confirmation, under the button, clearing itself after 2.5s. `role`
          and `aria-live` so it is announced rather than being a green flash
          only sighted users get. */}
      {saved && !error && (
        <p className="buy-target-editor__saved" role="status" aria-live="polite">
          Saved
        </p>
      )}

      {duplicates.length > 0 && (
        <p className="buy-target-editor__error" role="alert">
          {duplicates.join(", ")} {duplicates.length === 1 ? "is" : "are"} in more than one
          band — each year can have only one target.
        </p>
      )}

      {incomplete && duplicates.length === 0 && (
        <p className="buy-target-editor__error" role="alert">
          Every band needs both years and a maximum price.
        </p>
      )}

      {error && (
        <p className="buy-target-editor__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default BuyTargetEditor;
